import { useEffect, useRef } from 'react'
import { INTEGRATION_TYPE } from '@lightspeed-payments/payments-terminal-sdk'
import { ApiRetailerPaymentType } from '@vend/types'
import { trackError } from '@vend/utilities'
import {
  usePaymentManager,
  useRegisterManager,
} from '../../../../ng-compat/hooks'
import { useAppSelector } from '../../../../state/hooks'
import { selectSaleLocalId } from '../../../../state/slices/currentSale/currentSaleSlice'
import { getFeatureFromCache } from '../../../../util/feature-cache'
import {
  getMonitoringInstance,
  LSPAY_TERMINAL_EVENT,
  sendLSPayTerminalEvent,
} from '../../../payments/lspay-payment-modal/helpers/terminalMonitoring'
import { SANDBOX_FLAG } from '../../../payments/lspay-payment-modal/helpers/lspayConstants'
import { PaymentData } from '../PaymentPanel'

export type RecoveryPaymentOptions = {
  intentId?: string
  registerSalePaymentId: string
  recoveryAmount?: string | number
}
export type LSPaymentProps = {
  lspayPaymentType: ApiRetailerPaymentType | undefined
  pay: (paymentType: ApiRetailerPaymentType, paymentData?: PaymentData) => void
}

/**
 * Hook to verify whether there's a need to recover a lspay transaction and initiate the recovery
 * Run once only when lspayPaymentType is set
 */
export const useTransactionRecovery = (props: LSPaymentProps) => {
  const { lspayPaymentType, pay } = props
  const paymentManager = usePaymentManager()
  const registerManager = useRegisterManager()
  const saleLocalId = useAppSelector(selectSaleLocalId)
  const ref = useRef(false)

  useEffect(() => {
    if (ref.current || !lspayPaymentType) {
      return
    }

    try {
      const {
        intentId,
        registerSalePaymentId,
        amount: recoveryAmount,
      } = paymentManager.checkAndRetriveActivePayment(saleLocalId)

      if (!registerSalePaymentId) {
        return
      }
      const recoveryPaymentOptions = {
        intentId,
        registerSalePaymentId,
        recoveryAmount,
      }

      const monitoringInstance = getMonitoringInstance(
        registerManager.getActiveOutlet().id,
        getFeatureFromCache(SANDBOX_FLAG),
        INTEGRATION_TYPE.CLOUD
      )
      sendLSPayTerminalEvent(
        monitoringInstance,
        LSPAY_TERMINAL_EVENT.RECOVERY_REQUESTED,
        {
          registerSaleId: saleLocalId,
          registerSalePaymentId,
          recoveryPaymentIntentId: intentId,
          source: 'paymentPanel',
        }
      )

      pay(lspayPaymentType, { recoveryPaymentOptions })
    } catch (error) {
      trackError('Failed to complete payment recovery for LSPay transaction', {
        error,
      })
    } finally {
      ref.current = true
    }
  }, [paymentManager, registerManager, lspayPaymentType, saleLocalId, pay])
}
